import { useState, useCallback } from 'react';
import { Helpers } from '../../../../utils/helpers';
import { IApiResponse } from '../../../../utils/constants/interfaces';

interface IDevSetupRequest {
  password: string;
  email: string;
}

const useDevSetup = () => {
  const [loading, setLoading] = useState(false);
  const [setupMessage, setSetupMessage] = useState('');
  const [devSetupRequest, setDevSetupRequest] = useState<IDevSetupRequest>({
    email: '',
    password: ''
  });
  const isRunningLocal = Helpers.isRunningLocal();

  const apiUrl = import.meta.env.VITE_APP_API_URL ?? '';
  const apiPost = useCallback(
    async <T>(endpoint: string, body: string): Promise<IApiResponse<T>> => {
      try {
        const headers: HeadersInit = {
          'Content-Type': 'application/json'
        };
        const requestOptions: RequestInit = {
          method: 'POST',
          headers: headers,
          body: body
        };
        const apiResponse = await fetch(`${apiUrl}${endpoint}`, requestOptions);
        if (apiResponse.status === 200) {
          return (await apiResponse.json()) as IApiResponse<T>;
        } else {
          return Helpers.createApiResponse<T>(apiResponse.status, apiResponse.statusText);
        }
      } catch (e) {
        const errorText = Helpers.getErrorMessage(e);
        return Helpers.createApiResponse<T>(500, errorText);
      }
    },
    [apiUrl]
  );

  const updatePassword = (value: string): void => {
    setSetupMessage('');
    setDevSetupRequest({ ...devSetupRequest, password: value });
  };
  const updateEmail = (value: string): void => {
    setSetupMessage('');
    setDevSetupRequest({ ...devSetupRequest, email: value });
  };

  const createUser = async () => {
    if (loading) return;
    setSetupMessage('');

    if (!Helpers.isValidEmail(devSetupRequest.email)) {
      setSetupMessage('Invalid email address.');
      setLoading(false);
      return;
    }

    const userResult = await apiPost<IDevSetupRequest>(
      '/dev/setup',
      JSON.stringify(devSetupRequest)
    );
    if (!userResult?.response || userResult.status.statusCode !== 200) {
      setSetupMessage('Unknown error setting up initial user.');
      setLoading(false);
      return;
    }

    //if we get here, the user should be created
    setLoading(false);
    location.href = '/login';
  };

  return {
    loading,
    setupMessage,
    isRunningLocal,
    devSetupRequest,
    createUser,
    updatePassword,
    updateEmail
  };
};

export default useDevSetup;
